import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";

export const useAppStore = defineStore("app", () => {
  const legendHidden = ref(false);
  const viewVideo = ref(true);
  // Seeded from the class index.html sets before first paint, so mounting doesn't flash light
  const darkThemeEnabled = ref(globalThis.document?.documentElement.classList.contains("dark") ?? false);

  // Filename of loaded log (pushed from legacy code)
  const logFilename = ref("");
  // Names of files currently being read/parsed (pushed from legacy code) - non-empty while a log
  // file is being loaded, so the UI can show a busy cursor and "<file> is being loaded..." message.
  const loadingFileNames = ref([]);
  // File.lastModified (epoch ms) of the loaded log file - used as a fallback timestamp source by
  // the Tuning Log feature when a flight controller has no RTC (see tuning_log.js:resolveLogDateTimes).
  const logFileLastModified = ref(null);

  // Status bar display strings (pushed from legacy code)
  const statusCraftName = ref("");
  const statusFirmwareInfo = ref("-");
  const statusCraftNameStatus = ref(null); // null | "match" | "mismatch"
  const statusCraftNameTooltip = ref("");
  const statusCells = ref("");
  const statusLooptime = ref("-");
  const statusLograte = ref("-");
  const statusFlightMode = ref("-");
  const statusMarkerOffset = ref("00:00.000");
  const statusViewerVersion = ref("-");
  const graphTimeDisplay = ref("1.0");
  const videoOffsetDisplay = ref("+0.0");

  // Dialog open states (shared between legacy JS and Vue)
  const graphConfigDialogOpen = ref(false);
  const headerDialogOpen = ref(false);
  const videoExportDialogOpen = ref(false);
  const settingsDialogOpen = ref(false);
  const keysDialogOpen = ref(false);
  const craftConfigDialogOpen = ref(false);
  const craftNameMismatchDialogOpen = ref(false);
  const craftNameMismatchMessage = ref("");
  const tuningLogDialogOpen = ref(false);
  const flightAnalysisDialogOpen = ref(false);
  const headspeedInfoDialogOpen = ref(false);

  // Callbacks registered by main.js (closure-dependent operations)
  const loadFiles = shallowRef(null);
  const newGraphConfig = shallowRef(null);
  const exportCsv = shallowRef(null);
  const exportGpx = shallowRef(null);
  const exportWorkspaces = shallowRef(null);
  const openNewWindow = shallowRef(null);
  const pauseForExport = shallowRef(null);
  const getVideoExportParams = shallowRef(null);
  const saveVideoConfig = shallowRef(null);
  const saveUserSettings = shallowRef(null);
  const refreshGraph = shallowRef(null);

  function setLegendHidden(hidden) {
    legendHidden.value = hidden;
  }

  function setViewVideo(visible) {
    viewVideo.value = visible;
  }

  return {
    legendHidden,
    viewVideo,
    darkThemeEnabled,
    logFilename,
    loadingFileNames,
    logFileLastModified,
    statusCraftName,
    statusFirmwareInfo,
    statusCraftNameStatus,
    statusCraftNameTooltip,
    statusCells,
    statusLooptime,
    statusLograte,
    statusFlightMode,
    statusMarkerOffset,
    statusViewerVersion,
    graphTimeDisplay,
    videoOffsetDisplay,
    graphConfigDialogOpen,
    headerDialogOpen,
    videoExportDialogOpen,
    settingsDialogOpen,
    keysDialogOpen,
    craftConfigDialogOpen,
    craftNameMismatchDialogOpen,
    craftNameMismatchMessage,
    tuningLogDialogOpen,
    flightAnalysisDialogOpen,
    headspeedInfoDialogOpen,
    loadFiles,
    newGraphConfig,
    exportCsv,
    exportGpx,
    exportWorkspaces,
    openNewWindow,
    pauseForExport,
    getVideoExportParams,
    saveVideoConfig,
    saveUserSettings,
    refreshGraph,
    setLegendHidden,
    setViewVideo,
  };
});
